import {
  MAP_BULLET_REGEX,
  MAP_COMMENT_PREFIX,
  MAP_COORDINATE_REGEX,
  MAP_DIRECTIVE_REGEX,
  MAP_LAT_MAX,
  MAP_LAT_MIN,
  MAP_LON_MAX,
  MAP_LON_MIN,
  MAP_MAX_INPUT_LENGTH,
  MAP_MAX_LINE_LENGTH,
  MAP_MAX_PINS,
  MAP_PIN_ICON_FIELD_REGEX,
  MAP_PIN_SEPARATOR,
  MAP_PIN_SEPARATOR_BARE,
  MAP_ZOOM_MAX,
  MAP_ZOOM_MIN,
} from './map-constants.js';
import { MapDirectiveKey, MapPinKind } from '../../domain/map/enums.js';
import type { MapPin, ParsedMap } from '../../domain/map/types.js';

/** The parser's own working state. It is never passed to a helper below —
 *  every helper is a pure function that returns a value, and only
 *  `parseMapBlock` mutates this, on its own local copy. */
interface ParseAccumulator {
  title: string | null;
  zoom: number | null;
  center: MapPin | null;
  pins: MapPin[];
  droppedPinCount: number;
}

/** Patch describing which directive fields one line changed. A key's
 *  absence means "leave that field alone" — distinct from an explicit
 *  `null`, which means the directive cleared it. */
interface DirectiveUpdate {
  title?: string | null;
  zoom?: number | null;
  center?: MapPin | null;
}

/** The three authored fields of one pin line, before defaults are applied. A
 *  field the author omitted — or wrote empty — is null: `Berlin :: HQ :: `
 *  says nothing about the place, which is the same as not saying it. */
interface PinFields {
  place: string;
  label: string | null;
  description: string | null;
}

/** A pin line with its trailing tagged icon field peeled off. `line` is what
 *  remains for the three-field split; `icon` is the tagged keyword, or null
 *  when the line carried none. */
interface IconExtraction {
  line: string;
  icon: string | null;
}

/** Strip indentation and one optional markdown bullet. Models emit both. */
function normalizeLine({ rawLine }: { rawLine: string }): string {
  return rawLine.trim().replace(MAP_BULLET_REGEX, '').trim();
}

function isSkippableLine({ line }: { line: string }): boolean {
  return line.length === 0 || line.startsWith(MAP_COMMENT_PREFIX);
}

/** `lat, lon` only when both are in range — `91, 181` is text, not a coordinate. */
function parseCoordinatePin({
  text,
  label,
  description,
  icon,
}: {
  text: string;
  label: string;
  description: string | null;
  icon: string | null;
}): MapPin | null {
  const match = text.match(MAP_COORDINATE_REGEX);
  if (!match) {
    return null;
  }

  const lat = Number(match[1]);
  const lon = Number(match[2]);
  const inRange =
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    lat >= MAP_LAT_MIN &&
    lat <= MAP_LAT_MAX &&
    lon >= MAP_LON_MIN &&
    lon <= MAP_LON_MAX;

  return inRange
    ? { kind: MapPinKind.Coordinates, lat, lon, label, description, icon }
    : null;
}

/** Prefer the spaced separator (` :: `); fall back to the bare `::` only when
 *  the spaced form is absent from the line. See MAP_PIN_SEPARATOR_BARE for
 *  why. ONE form is chosen for the whole line, so a line is read under a
 *  single grammar rather than a per-field mixture. */
function findPinSeparator({ line }: { line: string }): string | null {
  if (line.includes(MAP_PIN_SEPARATOR)) {
    return MAP_PIN_SEPARATOR;
  }
  return line.includes(MAP_PIN_SEPARATOR_BARE) ? MAP_PIN_SEPARATOR_BARE : null;
}

function trimmedOrNull({ text }: { text: string }): string | null {
  const trimmed = text.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Peel a trailing `icon: <keyword>` field off a pin line.
 *
 * The icon is TAGGED rather than positional so it can never collide with the
 * description, which is prose and owns every untagged trailing segment:
 * `A :: B :: C :: D` still describes `C :: D`, while
 * `A :: B :: C :: icon: hotel` yields the description `C` and the icon
 * `hotel`. Only the LAST segment is examined — an `icon:`-shaped segment
 * earlier in the line is ordinary field text — and a valueless `icon:`
 * segment does not match (see MAP_PIN_ICON_FIELD_REGEX), so it stays inside
 * the line exactly as it parsed before this field existed.
 */
function extractIconField({
  line,
  separator,
}: {
  line: string;
  separator: string | null;
}): IconExtraction {
  // A line with no separator has no fields at all, so a bare `icon: hotel`
  // line remains a place named "icon: hotel" — the icon grammar never
  // reinterprets a line that already parsed as a plain place.
  if (!separator) {
    return { line, icon: null };
  }

  const tailIndex = line.lastIndexOf(separator);
  const tail = line.slice(tailIndex + separator.length).trim();
  const match = tail.match(MAP_PIN_ICON_FIELD_REGEX);
  if (!match) {
    return { line, icon: null };
  }

  return {
    line: line.slice(0, tailIndex),
    icon: trimmedOrNull({ text: match[1] ?? '' }),
  };
}

/**
 * `place :: label :: description`, split into AT MOST three parts.
 *
 * The last field is prose, so a fourth separator and everything after it stay
 * inside the description: `A :: B :: C :: D` describes `C :: D` rather than
 * dropping the tail. Both trailing fields are optional and independently so —
 * the first split is the one that decides what gets geocoded, and it behaves
 * exactly as it did before this field existed.
 */
function splitPinFields({
  line,
  separator,
}: {
  line: string;
  separator: string | null;
}): PinFields {
  // The form was chosen once, on the line as it was written (see parsePin),
  // so the precedence rule is exactly the one it has always been. Extracting
  // the icon may have removed the line's ONLY spaced separator, and
  // re-choosing here would let the bare fallback re-split an embedded `::`
  // inside a place name (`Café::Bar, Berlin :: icon: coffee`) — so absence of
  // the chosen form is the one-field form, never a re-choice. The SPLIT then
  // runs against a one-space pad: `normalizeLine` has already trimmed the
  // line, so a trailing empty field arrives as ` ::` with the separator's own
  // trailing space gone, and without the pad it would glue itself onto the
  // field before it (`HQ ::` as a label) instead of reading as an omitted
  // field.
  if (!separator || !line.includes(separator)) {
    return { place: line.trim(), label: null, description: null };
  }

  const padded = `${line} `;
  const labelIndex = padded.indexOf(separator);
  const afterPlace = padded.slice(labelIndex + separator.length);
  const descriptionIndex = afterPlace.indexOf(separator);
  const hasDescription = descriptionIndex >= 0;

  return {
    place: padded.slice(0, labelIndex).trim(),
    label: trimmedOrNull({
      text: hasDescription ? afterPlace.slice(0, descriptionIndex) : afterPlace,
    }),
    description: hasDescription
      ? trimmedOrNull({ text: afterPlace.slice(descriptionIndex + separator.length) })
      : null,
  };
}

/** One pin line. Only `place` is ever a lookup input; the label, the
 *  description, and the icon are display text and never reach a geocoder. */
function parsePin({ line }: { line: string }): MapPin | null {
  // ONE separator form is chosen on the line as authored — icon field
  // included — and shared by the icon extraction and the field split, so the
  // whole line is read under a single grammar rather than a per-stage
  // mixture.
  const separator = findPinSeparator({ line });
  const extracted = extractIconField({ line, separator });
  const fields = splitPinFields({ line: extracted.line, separator });
  if (fields.place.length === 0) {
    return null;
  }

  const label = fields.label ?? fields.place;
  const description = fields.description;
  const icon = extracted.icon;

  return (
    parseCoordinatePin({ text: fields.place, label, description, icon }) ?? {
      kind: MapPinKind.Place,
      place: fields.place,
      label,
      description,
      icon,
    }
  );
}

/** Empty (or whitespace-only) resolves to null — auto-fit — never to 0,
 *  which `Number('')` would otherwise coerce to and which is itself a
 *  valid zoom level. */
function parseZoom({ rawValue }: { rawValue: string }): number | null {
  const trimmed = rawValue.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const zoom = Number(trimmed);
  const valid =
    Number.isInteger(zoom) && zoom >= MAP_ZOOM_MIN && zoom <= MAP_ZOOM_MAX;
  return valid ? zoom : null;
}

function resolveDirectiveUpdate({
  key,
  rawValue,
}: {
  key: string;
  rawValue: string;
}): DirectiveUpdate {
  const value = rawValue.trim();
  const directiveKey = key.toLowerCase() as MapDirectiveKey;

  if (directiveKey === MapDirectiveKey.Title) {
    return { title: value.length > 0 ? value : null };
  }
  if (directiveKey === MapDirectiveKey.Zoom) {
    return { zoom: parseZoom({ rawValue: value }) };
  }
  if (directiveKey === MapDirectiveKey.Center) {
    return { center: value.length > 0 ? parsePin({ line: value }) : null };
  }
  // MapDirectiveKey.Route is reserved for v2 — recognized so it never
  // becomes a pin, and otherwise ignored.
  return {};
}

export function parseMapBlock(raw: string): ParsedMap | null {
  // Defensive ceiling on the whole block — a pathological block is never
  // even split into lines past this point. See MAP_MAX_INPUT_LENGTH.
  const bounded =
    raw.length > MAP_MAX_INPUT_LENGTH ? raw.slice(0, MAP_MAX_INPUT_LENGTH) : raw;

  const target: ParseAccumulator = {
    title: null,
    zoom: null,
    center: null,
    pins: [],
    droppedPinCount: 0,
  };

  bounded.split('\n').forEach((rawLine) => {
    // Defensive ceiling per line — see MAP_MAX_LINE_LENGTH.
    if (rawLine.length > MAP_MAX_LINE_LENGTH) {
      return;
    }

    const line = normalizeLine({ rawLine });
    if (isSkippableLine({ line })) {
      return;
    }

    const directiveMatch = line.match(MAP_DIRECTIVE_REGEX);
    if (directiveMatch) {
      const update = resolveDirectiveUpdate({
        key: directiveMatch[1] ?? '',
        rawValue: directiveMatch[2] ?? '',
      });
      if ('title' in update) {
        target.title = update.title ?? null;
      }
      if ('zoom' in update) {
        target.zoom = update.zoom ?? null;
      }
      if ('center' in update) {
        target.center = update.center ?? null;
      }
      return;
    }

    const pin = parsePin({ line });
    if (!pin) {
      return;
    }
    if (target.pins.length >= MAP_MAX_PINS) {
      target.droppedPinCount += 1;
      return;
    }
    target.pins.push(pin);
  });

  return target.pins.length > 0 ? target : null;
}
